import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
    TenantInfo,
    TenantStats,
    getStoredTenantContext,
    storeTenantContext,
    clearTenantContext,
    getApplicationType,
} from '../utils/tenant-detection';
import httpClient from '@/api/httpClient';

export type TenantErrorType = 'network' | 'not_found' | 'server' | 'unknown' | null;

interface TenantContextType {
    tenant: TenantInfo | null;
    stats: TenantStats | null;
    isLoading: boolean;
    error: string | null;
    errorType: TenantErrorType;
    refreshTenant: () => Promise<void>;
    refreshStats: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
    children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
    const [tenant, setTenant] = useState<TenantInfo | null>(null);
    const [stats, setStats] = useState<TenantStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [errorType, setErrorType] = useState<TenantErrorType>(null);

    // Fetch tenant context from API
    const fetchTenantContext = async () => {
        try {
            setIsLoading(true);
            setError(null);
            setErrorType(null);

            // Use the public system configuration endpoint instead of tenant-context
            // This endpoint doesn't require authentication and returns tenant info
            const response = await httpClient.get('/system-configuration');
            const data = response.data;

            if (data.success) {
                // Determine if this is an admin tenant based on the application type
                const appType = getApplicationType();
                const isAdminTenant = appType === 'admin';

                // Transform the system configuration data to match TenantInfo interface
                const tenantInfo: TenantInfo = {
                    tenantId: data.data.tenant_slug, // Use tenant_slug as tenantId
                    tenantSlug: data.data.tenant_slug,
                    tenantName: data.data.tenant_name,
                    systemName: data.data.system_name,
                    customDomain: data.data.custom_domain,
                    isActive: true, // Assume active if we got data
                    isAdminTenant: isAdminTenant,
                };

                setTenant(tenantInfo);
                storeTenantContext(tenantInfo);
            } else {
                throw new Error(data.message || 'Failed to fetch tenant context');
            }
        } catch (err: any) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
            console.error('Failed to fetch tenant context:', err);

            // Classify the error type
            if (
                errorMessage === 'No response received from server' ||
                err?.code === 'ERR_NETWORK' ||
                err?.code === 'ECONNREFUSED'
            ) {
                setErrorType('network');
            } else if (err?.response?.status === 404 || err?.response?.status === 422) {
                setErrorType('not_found');
            } else if (err?.response?.status >= 500) {
                setErrorType('server');
            } else {
                setErrorType('unknown');
            }

            // Fallback to stored context
            const storedTenant = getStoredTenantContext();
            if (storedTenant) {
                setTenant(storedTenant);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch tenant statistics
    const fetchTenantStats = async () => {
        try {
            const appType = getApplicationType();
            const endpoint =
                appType === 'admin'
                    ? '/tenant-manage/tenant-context/stats'
                    : `/${appType}/tenant-context/stats`;

            const response = await httpClient.get(endpoint);
            const data = response.data;

            if (data.success) {
                setStats(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch tenant stats');
            }
        } catch (err) {
            console.error('Failed to fetch tenant stats:', err);
        }
    };

    // Refresh tenant context
    const refreshTenant = async () => {
        await fetchTenantContext();
    };

    // Refresh tenant statistics
    const refreshStats = async () => {
        await fetchTenantStats();
    };

    // Initialize tenant context on mount
    useEffect(() => {
        const initializeTenant = async () => {
            // First, try to get stored context
            const storedTenant = getStoredTenantContext();
            if (storedTenant) {
                setTenant(storedTenant);
            }

            // Then fetch fresh context from API
            await fetchTenantContext();
            await fetchTenantStats();
        };

        initializeTenant();
    }, []);

    // Clear context on logout
    useEffect(() => {
        const handleLogout = () => {
            clearTenantContext();
            setTenant(null);
            setStats(null);
        };

        // Listen for logout events
        window.addEventListener('logout', handleLogout);

        return () => {
            window.removeEventListener('logout', handleLogout);
        };
    }, []);

    const value: TenantContextType = {
        tenant,
        stats,
        isLoading,
        error,
        errorType,
        refreshTenant,
        refreshStats,
    };

    return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextType {
    const context = useContext(TenantContext);
    if (context === undefined) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
}

// Hook for tenant-aware API calls
export function useTenantApi() {
    const { tenant } = useTenant();

    const makeRequest = async (url: string, options: RequestInit = {}) => {
        if (!tenant) {
            throw new Error('No tenant context available');
        }

        const headers = {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenant.tenantId,
            ...options.headers,
        };

        return fetch(url, {
            ...options,
            headers,
        });
    };

    return { makeRequest, tenant };
}
